import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND } from "../data/content";
import { Ornament, SunburstFan } from "./Ornament";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const [offset, setOffset] = useState(0);
  const [curtainsOpen, setCurtainsOpen] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setCurtainsOpen(true), 350);
    return () => clearTimeout(t);
  }, []);

  // Start the video only AFTER the curtains have fully opened,
  // so guests first see the still golden-performer poster, then the reel.
  useEffect(() => {
    if (!curtainsOpen) return;
    const t = setTimeout(() => {
      const v = videoRef.current;
      if (v) {
        v.play().catch(() => {});
        setVideoStarted(true);
      }
    }, 2000); // 1.6s curtain + 400ms breathing room
    return () => clearTimeout(t);
  }, [curtainsOpen]);

  // Subtle parallax scroll
  useEffect(() => {
    const onScroll = () => setOffset(Math.min(window.scrollY, 800));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden grain"
      style={{ background: "var(--lu-black)" }}
      data-testid="hero-section"
    >
      {/* Background video with poster fallback + parallax */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{
          transform: `translate3d(0, ${offset * 0.25}px, 0) scale(${1 + offset * 0.00015})`,
        }}
      >
        <video
          ref={videoRef}
          className={`hero-video transition-opacity duration-700 ${
            videoStarted ? "opacity-95" : "opacity-0"
          }`}
          muted
          loop
          playsInline
          preload="auto"
          poster="/generated/hero.jpg"
          style={{ filter: "contrast(1.08) saturate(1.15) brightness(1.02)" }}
          data-testid="hero-video"
        >
          <source src="/generated/hero.mp4" type="video/mp4" />
        </video>

        {/* Poster image visible until the video kicks in — user first sees
            the still golden-performer editorial as the curtains part. */}
        <img
          src="/generated/hero.jpg"
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            videoStarted ? "opacity-0" : "opacity-95"
          }`}
          style={{ filter: "contrast(1.08) saturate(1.15) brightness(1.02)" }}
          data-testid="hero-poster"
        />

        {/* Darkening for contrast — lighter so video reads clearer */}
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--lu-black)]/25 via-[var(--lu-black)]/15 to-[var(--lu-black)]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(10,10,10,0.55)_90%)]" />
      </div>

      {/* Sunburst rays */}
      <SunburstFan
        className="absolute -top-10 left-1/2 -translate-x-1/2 w-[1100px] opacity-30 pointer-events-none"
        style={{ transform: `translate3d(-50%, ${offset * -0.15}px, 0)` }}
      />

      {/* Thin corner frame */}
      <div className="pointer-events-none absolute inset-8 lg:inset-14 border border-[var(--lu-gold)]/20" />
      <div className="pointer-events-none absolute top-8 lg:top-14 left-8 lg:left-14 w-10 h-10 border-t-2 border-l-2 border-[var(--lu-gold)]" />
      <div className="pointer-events-none absolute top-8 lg:top-14 right-8 lg:right-14 w-10 h-10 border-t-2 border-r-2 border-[var(--lu-gold)]" />
      <div className="pointer-events-none absolute bottom-8 lg:bottom-14 left-8 lg:left-14 w-10 h-10 border-b-2 border-l-2 border-[var(--lu-gold)]" />
      <div className="pointer-events-none absolute bottom-8 lg:bottom-14 right-8 lg:right-14 w-10 h-10 border-b-2 border-r-2 border-[var(--lu-gold)]" />

      {/* Content */}
      <div
        className="relative z-10 container-lux pt-40 lg:pt-48 pb-28 flex flex-col items-center text-center"
        style={{ transform: `translate3d(0, ${offset * -0.1}px, 0)` }}
      >
        <p className="kicker rise" data-testid="hero-kicker">Since 2016 &nbsp;·&nbsp; Creative Entertainment Atelier</p>

        <h1
          className="mt-10 h-editorial text-[80px] sm:text-[112px] lg:text-[168px] leading-[0.88] text-[var(--lu-ivory)] tracking-[0.02em] rise rise-delay-1"
          data-testid="hero-title"
        >
          <span className="block gold-shimmer" style={{ fontWeight: 500 }}>
            {BRAND.name}
          </span>
        </h1>

        <div className="mt-8 flex items-center gap-6 rise rise-delay-2">
          <span className="h-px w-20 bg-[var(--lu-gold)]" />
          <span className="font-micro text-[11px] text-ivory-85">
            {BRAND.tagline}
          </span>
          <span className="h-px w-20 bg-[var(--lu-gold)]" />
        </div>

        <div className="mt-20 max-w-4xl rise rise-delay-3">
          <p className="eyebrow">— {BRAND.hero_title_top}</p>
          <p className="h-editorial text-[56px] sm:text-[76px] lg:text-[104px] text-[var(--lu-ivory)] mt-3">
            {BRAND.hero_title_mid}
          </p>
          <p className="h-editorial text-[56px] sm:text-[76px] lg:text-[104px] text-[var(--lu-gold)]">
            {BRAND.hero_title_bottom}
          </p>
          <p className="font-serif italic text-[44px] sm:text-[60px] text-[var(--lu-ivory)] mt-4">
            {BRAND.hero_script}.
          </p>
        </div>

        {/* Signature promise line */}
        <div className="mt-14 max-w-4xl rise rise-delay-4" data-testid="hero-promise">
          <div className="flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-[var(--lu-gold)]" />
            <p className="kicker">Our Promise</p>
            <span className="h-px w-10 bg-[var(--lu-gold)]" />
          </div>
          <p className="mt-6 font-serif italic text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.1] text-[var(--lu-gold)]">&ldquo;The night of a lifetime.&rdquo;</p>
        </div>

        <Ornament className="mt-16 rise rise-delay-4" />

        <p
          className="mt-8 kicker rise rise-delay-4"
          data-testid="hero-tagline"
        >Performances &nbsp;·&nbsp; Creative Concepts &nbsp;·&nbsp; Immersive Experiences</p>

        <p className="mt-12 max-w-2xl text-[17px] leading-[1.85] text-ivory-75 rise rise-delay-5 font-light">
          {BRAND.hero_sub}
        </p>

        <div className="mt-16 flex flex-col sm:flex-row gap-5 rise rise-delay-5">
          <button
            onClick={() => navigate("/contact")}
            className="btn-gold"
            data-testid="hero-cta-proposal"
          >
            Request Proposal
          </button>
          <button
            onClick={() => navigate("/contact")}
            className="btn-gold-outline"
            data-testid="hero-cta-discovery"
          >Close Discovery Call</button>
        </div>
      </div>

      {/* Scroll cue */}
      <button
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-gold-80 hover:text-[var(--lu-gold)] transition-colors floaty"
        data-testid="hero-scroll-cue"
      >
        <span className="font-micro text-[10px]">Find out</span>
        <ArrowDown size={16} />
      </button>

      {/* Curtain reveal overlay */}
      <div className="pointer-events-none absolute inset-0 z-[60] overflow-hidden" aria-hidden data-testid="hero-curtains">
        <div className="curtain-panel curtain-left"  data-open={curtainsOpen} />
        <div className="curtain-panel curtain-right" data-open={curtainsOpen} />
      </div>
    </section>
  );
}
